import React, { useState, useEffect } from 'react';
import { Card, Box, CardHeader, TextField, CardContent, Divider, Grid, Typography, Button, CircularProgress, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import Swal from 'sweetalert2';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const AddCareerPage = () => {
  const [newCareerData, setNewCareerData] = useState({
    title: '',
    location: '',
    bu_master: '',
    type: '',
    deadline: '',
    description: '',
    is_active: true,  
  });
  const [buMasters, setBuMasters] = useState([]);
  const [loading, setLoading] = useState(false); // New loading state
  const navigate = useNavigate();

  useEffect(() => {
    fetchBuMasters();
  }, []);

  useEffect(() => {
    if (newCareerData.deadline) {
      const currentDate = new Date();
      const deadlineDate = new Date(newCareerData.deadline);

      // If deadline is passed, set is_active to false, otherwise true
      setNewCareerData((prevData) => ({
        ...prevData,
        is_active: deadlineDate > currentDate, // True if deadline is in the future
      }));
    }
  }, [newCareerData.deadline]);  // Trigger this effect when the deadline changes

  const fetchBuMasters = async () => {
    try {
      const response = await axios.get('https://3wzg6m6x-5000.asse.devtunnels.ms/api/bu_master');
      setBuMasters(response.data);
    } catch (error) {
      console.error('Error fetching BU Masters:', error);
    }
  };

  const handleAddCareer = async () => {
    // Validate that all required fields are filled
    if (!newCareerData.title || !newCareerData.location || !newCareerData.bu_master || !newCareerData.type || !newCareerData.deadline || !newCareerData.description) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing fields!',
        text: 'Please fill in all required fields before submitting.',
      });
      return;
    }

    setLoading(true); // Start loading
    const formData = new FormData();

    // Append data from the form
    formData.append('title', newCareerData.title);
    formData.append('location', newCareerData.location);
    formData.append('id_bu_master', newCareerData.bu_master);
    formData.append('type', newCareerData.type);
    formData.append('deadline', newCareerData.deadline);
    formData.append('description', newCareerData.description);
    formData.append('is_active', newCareerData.is_active);

    try {
      const token = localStorage.getItem('token');
      await axios.post('https://3wzg6m6x-5000.asse.devtunnels.ms/api/career/add', formData, {
        headers: {
          Authorization: `Bearer ${token}`
        },
      });

      Swal.fire({
        icon: 'success',
        title: 'Career added successfully!',
      });

      navigate('/compro/career');
    } catch (error) {
      console.log('Error response:', error.response);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to add career!',
      });
    } finally {
      setLoading(false); // End loading
    }
  };

  return (
    <Card sx={{ borderRadius: 2, boxShadow: 3, padding: 3 }}>
      <CardHeader
        title={<Typography variant="h5" sx={{ textAlign: 'center', fontWeight: 'bold' }}>Add Career</Typography>}
        sx={{ paddingBottom: 2 }}
      />
      <Divider />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Title"
              value={newCareerData.title}
              onChange={(e) => setNewCareerData({ ...newCareerData, title: e.target.value })}
              variant="outlined"
              sx={{ borderRadius: 2 }}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Location"
              value={newCareerData.location}
              onChange={(e) => setNewCareerData({ ...newCareerData, location: e.target.value })}
              variant="outlined"
              sx={{ borderRadius: 2 }}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="outlined" sx={{ borderRadius: 2 }} required>
              <InputLabel>BU Master</InputLabel>
              <Select
                value={newCareerData.bu_master}
                onChange={(e) => setNewCareerData({ ...newCareerData, bu_master: e.target.value })}
                label="BU Master"
              >
                {buMasters.map((bu) => (
                  <MenuItem key={bu.id} value={bu.id}>{bu.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="outlined" sx={{ borderRadius: 2 }} required>
              <InputLabel>Type</InputLabel>
              <Select
                value={newCareerData.type}
                onChange={(e) => setNewCareerData({ ...newCareerData, type: e.target.value })}
                label="Type"
              >
                <MenuItem value="part_time">Part Time</MenuItem>
                <MenuItem value="full_time">Full Time</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Deadline"
              type="date"
              value={newCareerData.deadline}
              onChange={(e) => setNewCareerData({ ...newCareerData, deadline: e.target.value })}
              variant="outlined"
              sx={{ borderRadius: 2 }}
              InputLabelProps={{
                shrink: true,
              }}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <ReactQuill
              value={newCareerData.description}
              onChange={(value) => setNewCareerData({ ...newCareerData, description: value })}
              modules={{
                toolbar: [
                  [{ 'header': '1' }, { 'header': '2' }, { 'font': [] }],
                  [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                  ['bold', 'italic', 'underline'],
                  ['link'],
                  [{ 'align': [] }],
                  ['image'],
                ],
              }}
              style={{ height: '150px', width: '100%' }}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 5 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddCareer}
                disabled={loading}
                startIcon={loading && <CircularProgress size={20} />} // Loading icon
              >
                {loading ? 'Saving...' : 'Save Career'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default AddCareerPage;
